import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomerOrdersAPI } from '../../Services/ECommerceAPI';
import { getShippingAddressLines, getAccountCustomer } from '../../Utils/orderFormat';
import { toast } from 'react-toastify';
import './CustomerOrders.css';

const CustomerOrders = () => {
    const { id } = useParams();
    const [orders, setOrders] = useState([]);
    const [detailOrder, setDetailOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const token = sessionStorage.getItem('token');
        if (id) {
            if (!token) {
                setLoading(false);
                setDetailOrder(null);
                return;
            }
            loadOrderDetail(parseInt(id, 10));
        } else {
            if (!token) {
                setLoading(false);
                navigate('/customer/login');
                return;
            }
            loadOrders();
        }
    }, [id]);

    const loadOrders = async () => {
        try {
            setLoading(true);
            const data = await CustomerOrdersAPI.getOrders();
            setOrders(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error loading orders:', error);
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    const loadOrderDetail = async (orderId) => {
        if (!orderId || Number.isNaN(orderId)) {
            setLoading(false);
            navigate('/orders');
            return;
        }
        try {
            setLoading(true);
            setDetailOrder(null);
            const data = await CustomerOrdersAPI.getOrderById(orderId);
            setDetailOrder(data);
        } catch (error) {
            console.error('Error loading order:', error);
            toast.error('Failed to load order');
            navigate('/orders');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="orders-loading">Loading...</div>;
    }

    if (id && !sessionStorage.getItem('token')) {
        return (
            <div className="customer-orders-container">
                <p>Please sign in to view order details.</p>
                <button type="button" className="shop-button" onClick={() => navigate('/customer/login')}>
                    Log in
                </button>
            </div>
        );
    }

    if (id && detailOrder) {
        const account = getAccountCustomer(detailOrder);
        const shippingLines = getShippingAddressLines(detailOrder);
        return (
            <div className="customer-orders-container order-detail-public">
                <button type="button" className="back-to-orders-btn" onClick={() => navigate('/orders')}>
                    ← Back to orders
                </button>
                <h1>Order #{detailOrder.orderNumber}</h1>
                <p className="order-detail-meta">
                    Placed on {new Date(detailOrder.orderDate).toLocaleString()}
                    <span className={`status-badge ${detailOrder.orderStatus?.statusName?.toLowerCase()}`}>
                        {detailOrder.orderStatus?.statusName}
                    </span>
                </p>

                <section className="public-order-section">
                    <h2>Customer contact</h2>
                    <div className="contact-lines">
                        <div className="contact-line">
                            <span className="contact-label">Name</span>
                            <span>{account ? `${account.firstName} ${account.lastName}`.trim() || account.email : '—'}</span>
                        </div>
                        <div className="contact-line">
                            <span className="contact-label">Email</span>
                            <span>{account?.email || '—'}</span>
                        </div>
                        <div className="contact-line">
                            <span className="contact-label">Account phone</span>
                            <span>{account?.phone || '—'}</span>
                        </div>
                        <div className="contact-line">
                            <span className="contact-label">Shipping phone</span>
                            <span>{detailOrder.shippingPhone || detailOrder.ShippingPhone || '—'}</span>
                        </div>
                    </div>
                </section>

                <section className="public-order-section">
                    <h2>Shipping address</h2>
                    <div className="shipping-address-block">
                        {shippingLines.map((line) => (
                            <div key={line.label} className="shipping-address-line">
                                <span className="shipping-line-label">{line.label}</span>
                                <span className="shipping-line-value">{line.value}</span>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="public-order-section">
                    <h2>Items</h2>
                    <ul className="public-order-items">
                        {(detailOrder.orderItems || []).map((item) => (
                            <li key={item.orderItemID}>
                                <span>{item.productName}</span>
                                <span>Qty {item.quantity}</span>
                                <span>${Number(item.totalPrice).toFixed(2)}</span>
                            </li>
                        ))}
                    </ul>
                </section>

                <section className="public-order-section totals-strip">
                    <div><span>Subtotal</span><strong>${Number(detailOrder.subTotal).toFixed(2)}</strong></div>
                    <div><span>Tax</span><strong>${Number(detailOrder.taxAmount).toFixed(2)}</strong></div>
                    <div><span>Shipping</span><strong>${Number(detailOrder.shippingAmount).toFixed(2)}</strong></div>
                    <div className="grand"><span>Total</span><strong>${Number(detailOrder.totalAmount).toFixed(2)}</strong></div>
                </section>
            </div>
        );
    }

    return (
        <div className="customer-orders-container">
            <h1>My Orders</h1>

            {orders.length === 0 ? (
                <div className="no-orders">
                    <h2>No orders yet</h2>
                    <p>Start shopping to see your orders here</p>
                    <button onClick={() => navigate('/shop')} className="shop-button">
                        Start Shopping
                    </button>
                </div>
            ) : (
                <div className="orders-list">
                    {orders.map((order) => (
                        <div key={order.orderID} className="order-card">
                            <div className="order-header">
                                <div>
                                    <h3>Order #{order.orderNumber}</h3>
                                    <p className="order-date">
                                        Placed on {new Date(order.orderDate).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="order-status">
                                    <span className={`status-badge ${order.orderStatus?.statusName?.toLowerCase()}`}>
                                        {order.orderStatus?.statusName}
                                    </span>
                                </div>
                            </div>

                            <div className="order-items-summary">
                                <p>{order.orderItems?.length || 0} item(s)</p>
                                <p className="order-total">Total: ${order.totalAmount.toFixed(2)}</p>
                            </div>

                            <div className="order-actions">
                                <button
                                    onClick={() => navigate(`/order/${order.orderID}`)}
                                    className="view-order-btn"
                                >
                                    View Details
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CustomerOrders;
